import React, { useState } from 'react';
import { Outlet, NavLink as RouterNavLink, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { supabase } from '../../config/supabase';
import { slugify } from '../../utils/slugify';
import { FiGrid, FiPackage, FiShoppingCart, FiCreditCard, FiDollarSign, FiHome, FiTag, FiLogOut, FiX, FiSettings, FiExternalLink } from 'react-icons/fi';
import { FiBell, FiMenu } from 'react-icons/fi';

const AdminContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f9fafb;
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
`;

const Overlay = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 40;
  }
`;

const Sidebar = styled.aside`
  width: 260px;
  background: white;
  border-right: ${props => props.$isArabic ? 'none' : '1px solid #e5e7eb'};
  border-left: ${props => props.$isArabic ? '1px solid #e5e7eb' : 'none'};
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  z-index: 50;
  
  @media (max-width: 768px) {
    position: fixed;
    left: ${props => props.$isArabic ? 'auto' : '0'};
    right: ${props => props.$isArabic ? '0' : 'auto'};
    top: 0;
    transform: translateX(${props => {
      if (props.$isOpen) return '0';
      return props.$isArabic ? '100%' : '-100%';
    }});
    transition: transform 0.3s ease-in-out;
    box-shadow: ${props => {
      if (!props.$isOpen) return 'none';
      return props.$isArabic ? '-4px 0 15px rgba(0, 0, 0, 0.1)' : '4px 0 15px rgba(0, 0, 0, 0.1)';
    }};
  }
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
`;

const LogoBox = styled.div`
  width: 48px;
  height: 48px;
  background: #5efba9;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: white;
  font-size: 18px;
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
`;

const UserStatus = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const SidebarHeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: none;
  
  &:hover {
    background: #f3f4f6;
  }
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileMenuButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: none;
  
  &:hover {
    background: #f3f4f6;
  }
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Nav = styled.nav`
  flex: 1;
  overflow-y: auto;
  padding: 20px 0;
`;

const NavLink = styled(RouterNavLink)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  color: #6b7280;
  text-decoration: none;
  font-size: 14px;
  transition: all 0.2s;
  border-radius: 8px;
  margin: 0 12px;
  
  &:hover {
    background: #f3f4f6;
    color: #1f2937;
  }
  
  &.active {
    background: #5efba9;
    color: #1f2937;
    font-weight: 600;
  }
`;

const VisitStoreButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: #5efba9;
  color: #1f2937;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  margin-top: 12px;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  
  &:hover {
    background: #4dd1a0;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(94, 251, 169, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TopBar = styled.header`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 16px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    gap: 8px;
  }
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  flex-shrink: 0;
  
  &:hover {
    background: #f3f4f6;
  }
  
  @media (max-width: 768px) {
    padding: 6px;
    font-size: 18px;
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  background: #5efba9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: white;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserNameTop = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #b91c1c;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 8px;
    
    span {
      display: none;
    }
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 32px;
  overflow-y: auto;
  overflow-x: hidden;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ContentInner = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: visible;
  min-width: 0;
  text-align: ${props => props.$isArabic ? 'right' : 'left'};
`;

const LanguageSelect = styled.select`
  padding: 6px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  transition: all 0.2s;

  &:hover {
    border-color: #5efba9;
  }

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 6px 8px;
    font-size: 12px;
  }
`;

export default function AdminLayout() {
  const { profile, session } = useAuthContext();
  const { language, setLanguage, isArabic } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  const [brandName, setBrandName] = useState(null);
  const [brandSlug, setBrandSlug] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const userInitials = profile?.first_name?.[0] || session?.user?.email?.[0] || 'A';
  
  // Close sidebar when route changes (mobile)
  React.useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Fetch admin's brand name
  React.useEffect(() => {
    const fetchBrandName = async () => {
      if (!session) return;

      try {
        // Get brand from brand_managers table with brand name and slug in single query
        const { data: brandManager, error: brandError } = await supabase
          .from('brand_managers')
          .select('brand_id, brand:brands(name, slug)')
          .eq('profile_id', session.user.id)
          .maybeSingle();

        if (brandManager && !brandError && brandManager.brand) {
          const name = brandManager.brand.name;
          const slug = brandManager.brand.slug || slugify(name);
          setBrandName(name);
          setBrandSlug(slug);
        } else {
          // Fallback: Try to find brand by created_by (use most recent if multiple)
          const { data: userBrands, error: brandsError } = await supabase
            .from('brands')
            .select('name, slug')
            .eq('created_by', session.user.id)
            .order('created_at', { ascending: false })
            .limit(1);

          if (!brandsError && userBrands && userBrands.length > 0) {
            const name = userBrands[0].name;
            const slug = userBrands[0].slug || slugify(name);
            setBrandName(name);
            setBrandSlug(slug);
            if (userBrands.length > 1) {
              console.warn('⚠️ User has multiple brands. Using the most recent one.');
            }
          }
        }
      } catch (error) {
        console.error('Error fetching admin brand name:', error);
      }
    };

    fetchBrandName();
  }, [session]);

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      // Always clear local session first (reliable)
      const { error: localError } = await supabase.auth.signOut();
      if (localError) {
        console.error('Error signing out (local):', localError);
      }

      // Best-effort revoke (non-blocking)
      supabase.auth.signOut({ scope: 'global' }).catch(() => {});

      // Hard redirect so guards reset immediately
      window.location.assign('/admin/login');
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Error signing out. Please try again.');
    } finally {
      setLoggingOut(false);
    }
  };

  // Get page title based on current route
  const getPageTitle = () => {
    if (location.pathname === '/admin') return isArabic ? 'لوحة التحكم' : 'Dashboard';
    if (location.pathname === '/admin/products') return isArabic ? 'قائمة المنتجات' : 'Product List';
    if (location.pathname === '/admin/orders') return isArabic ? 'الطلبات' : 'Orders';
    if (location.pathname === '/admin/status') return isArabic ? 'الحالة' : 'Status';
    if (location.pathname === '/admin/voucher-codes') return isArabic ? 'أكواد الخصم' : 'Voucher Codes';
    if (location.pathname === '/admin/payments') return isArabic ? 'المدفوعات' : 'Payments';
    if (location.pathname === '/admin/bank-details') return isArabic ? 'التفاصيل المصرفية' : 'Bank Details';
    if (location.pathname === '/admin/brand-settings') return isArabic ? 'إعدادات العلامة التجارية' : 'Brand Settings';
    return isArabic ? 'لوحة التحكم' : 'Dashboard';
  };

  return (
    <AdminContainer dir={isArabic ? 'rtl' : 'ltr'}>
      <Overlay $isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      <Sidebar $isOpen={sidebarOpen} $isArabic={isArabic}>
        <SidebarHeader>
          <SidebarHeaderTop>
            <LogoBox>W</LogoBox>
            <UserInfo>
              <UserName>Wasil</UserName>
              <UserStatus>{brandName || 'Verified'}</UserStatus>
            </UserInfo>
            <MenuButton onClick={() => setSidebarOpen(false)}>
              <FiX />
            </MenuButton>
          </SidebarHeaderTop>
          {brandSlug && (
            <VisitStoreButton 
              href={`https://wasl.om/${brandSlug}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FiExternalLink /> {isArabic ? 'زيارة المتجر' : 'Visit Store'}
            </VisitStoreButton>
          )}
        </SidebarHeader>
        <Nav>
          <NavLink to="/admin" end>
            <FiGrid /> {isArabic ? 'لوحة التحكم' : 'Dashboard'}
          </NavLink>
          <NavLink to="/admin/products">
            <FiPackage /> {isArabic ? 'منتجاتي' : 'My Product'}
          </NavLink>
          <NavLink to="/admin/orders">
            <FiShoppingCart /> {isArabic ? 'الطلبات' : 'Orders'}
          </NavLink>
          <NavLink to="/admin/status">
            <FiCreditCard /> {isArabic ? 'الحالة' : 'Status'}
          </NavLink>
          <NavLink to="/admin/voucher-codes">
            <FiTag /> {isArabic ? 'أكواد الخصم' : 'Voucher Codes'}
          </NavLink>
          <NavLink to="/admin/payments">
            <FiDollarSign /> {isArabic ? 'المدفوعات' : 'Payments'}
          </NavLink>
          <NavLink to="/admin/bank-details">
            <FiHome /> {isArabic ? 'التفاصيل المصرفية' : 'Bank Details'}
          </NavLink>
          <NavLink to="/admin/brand-settings">
            <FiSettings /> {isArabic ? 'إعدادات العلامة التجارية' : 'Brand Settings'}
          </NavLink>
        </Nav>
      </Sidebar>
      <MainContent>
        <TopBar>
          <TopBarLeft>
            <MobileMenuButton onClick={() => setSidebarOpen(true)}>
              <FiMenu />
            </MobileMenuButton>
            <PageTitle>{getPageTitle()}</PageTitle>
          </TopBarLeft>
          <TopBarActions>
            <IconButton>
              <FiBell />
            </IconButton>
            <LanguageSelect value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option value="en">EN</option>
              <option value="ar">AR</option>
            </LanguageSelect>
            <UserProfile>
              <Avatar>{userInitials}</Avatar>
              <UserDetails>
                <UserNameTop>{profile?.first_name || (isArabic ? 'مستخدم' : 'User')}</UserNameTop>
                <UserEmail>{session?.user?.email}</UserEmail>
              </UserDetails>
            </UserProfile>
            <LogoutButton onClick={handleLogout} disabled={loggingOut}>
              <FiLogOut />
              <span>{loggingOut ? (isArabic ? 'جاري...' : '...') : (isArabic ? 'خروج' : 'Logout')}</span>
            </LogoutButton>
          </TopBarActions>
        </TopBar>
        <Content>
          <ContentInner dir={isArabic ? 'rtl' : 'ltr'} $isArabic={isArabic}>
            <Outlet />
          </ContentInner>
        </Content>
      </MainContent>
    </AdminContainer>
  );
}
